/**
 * TanStack Query hooks for the /now page data layer.
 *
 * Each hook wraps one API fetch function with appropriate stale times
 * and query key structure. The enabled flag prevents queries from firing
 * when siteId is empty (before the sites list resolves).
 *
 * Query key conventions:
 *   ['sites']                       - site list (60s stale)
 *   ['live-state', siteId]          - live sensor/zone state (5s stale)
 *   ['deterrence', siteId, window]  - deterrence KPIs (30s stale)
 *   ['alerts', siteId, since]       - recent alert list (10s stale)
 *
 * Plan 03-06 will invalidate live-state and alerts keys on WebSocket push.
 */
import { useQuery } from '@tanstack/react-query';
import {
  fetchSites,
  fetchLiveState,
  fetchDeterrenceKPIs,
  fetchRecentAlerts,
  fetchSiteDetail,
  fetchDeterrenceBreakdown,
} from './api';
import type {
  DeterrenceBreakdownPeriod,
  DeterrenceBreakdownOutcomeFilter,
} from './schemas';

/**
 * Drawer page size for the deterrence breakdown drill-in.
 *
 * 25 rows fits comfortably in the 480px-wide side drawer at typical
 * desktop heights without overflowing the viewport. The server caps
 * limit at 200; 25 stays well within budget and pages quickly.
 */
const BREAKDOWN_PAGE_SIZE = 25;

/**
 * Fetches the list of sites for the caller's org.
 *
 * Used by NowPage to select the primary site (Phase 3 picks sites[0]).
 * Multi-site site selection UI ships Phase 4.
 */
export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: fetchSites,
    staleTime: 60_000,
  });
}

/**
 * Fetches the live state (zones + sensors) for a specific site.
 *
 * Disabled when siteId is empty; fires once siteId is available from useSites.
 * Refreshed every 5 seconds to stay reasonably current without WebSocket.
 *
 * @param siteId - UUID of the target site (empty string disables the query)
 */
export function useLiveState(siteId: string) {
  return useQuery({
    queryKey: ['live-state', siteId],
    queryFn: () => fetchLiveState(siteId),
    enabled: !!siteId,
    staleTime: 5_000,
  });
}

/**
 * Fetches deterrence KPIs for the hero strip.
 *
 * @param siteId - UUID of the target site (empty string disables the query)
 * @param window - Aggregation window; defaults to 'week'
 */
export function useDeterrenceKPIs(siteId: string, window: 'week' | 'month' | 'all' = 'week') {
  return useQuery({
    queryKey: ['deterrence', siteId, window],
    queryFn: () => fetchDeterrenceKPIs(siteId, window),
    enabled: !!siteId,
    staleTime: 30_000,
  });
}

/**
 * Fetches recent alerts for the event ribbon.
 *
 * @param siteId - UUID of the target site (empty string disables the query)
 * @param since - ISO 8601 duration string; defaults to 'PT2H'
 */
export function useRecentAlerts(siteId: string, since: string = 'PT2H') {
  return useQuery({
    queryKey: ['alerts', siteId, since],
    queryFn: () => fetchRecentAlerts(siteId, since),
    enabled: !!siteId,
    staleTime: 10_000,
  });
}

/**
 * Fetches full site detail for a specific site by ID.
 *
 * Required export for Plan 03-07: FrozenMapSnapshot uses useSiteDetail to
 * obtain site.location.lat/lng for centering the MapLibre map.
 *
 * Query key: ['site-detail', siteId]
 * Stale time: 60s (site location rarely changes mid-session)
 * Disabled when siteId is empty.
 *
 * @param siteId - UUID of the target site (empty string disables the query)
 * @returns TanStack Query result containing SiteDetail including optional location
 */
export function useSiteDetail(siteId: string) {
  return useQuery({
    queryKey: ['site-detail', siteId],
    queryFn: () => fetchSiteDetail(siteId),
    enabled: !!siteId,
    staleTime: 60_000,
  });
}

/**
 * Filter state for the deterrence breakdown drawer pager.
 *
 * Centralized so the drawer + tests share the same shape. Page is
 * zero-indexed; offset is derived as page * BREAKDOWN_PAGE_SIZE.
 */
export interface DeterrenceBreakdownFilters {
  period: DeterrenceBreakdownPeriod;
  outcome: DeterrenceBreakdownOutcomeFilter;
  page: number;
}

/**
 * Fetches the paginated breakdown for the Performance drawer.
 *
 * Plan 12-11 consumer of the Plan 12-10 endpoint. Disabled when
 * `site_id` is null so the drawer can mount without firing a request
 * before it opens (the drawer passes null while closed).
 *
 * The query key includes filters verbatim so changing period / outcome
 * / page each yields a distinct cache slot; flipping back to a previous
 * filter combo serves the cached page without a network round-trip.
 *
 * @param site_id - Target site UUID or null to disable the query
 * @param filters - Period + outcome filter + zero-indexed page
 */
export function useDeterrenceBreakdown(
  site_id: string | null,
  filters: DeterrenceBreakdownFilters,
) {
  return useQuery({
    queryKey: ['now', 'deterrence-breakdown', site_id, filters],
    queryFn: () =>
      fetchDeterrenceBreakdown({
        site_id: site_id!,
        period: filters.period,
        outcome: filters.outcome,
        limit: BREAKDOWN_PAGE_SIZE,
        offset: filters.page * BREAKDOWN_PAGE_SIZE,
      }),
    enabled: Boolean(site_id),
    // 15s stale: the breakdown is event-history data that updates as
    // new entry events arrive. A 15s window balances "fresh enough for
    // a live-feeling drawer" against "don't refetch on every render".
    staleTime: 15_000,
  });
}

/**
 * Page size constant re-exported so consumers (the drawer pager) can
 * derive the total page count consistently.
 */
export const DETERRENCE_BREAKDOWN_PAGE_SIZE = BREAKDOWN_PAGE_SIZE;
